
import React from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '@/data/blogData';
import { Search, Tag as TagIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const BlogSidebar = () => {
  // Get all unique categories
  const categories = Array.from(new Set(blogPosts.map(post => post.category)));
  
  // Get recent posts (4)
  const recentPosts = [...blogPosts]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 4);
  
  // Get all unique tags
  const allTags = blogPosts.flatMap(post => post.tags);
  const uniqueTags = Array.from(new Set(allTags));

  return (
    <aside className="space-y-8">
      {/* Search Widget */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-xl font-serif font-bold mb-4">Search</h3>
        <div className="relative">
          <Input
            type="text"
            placeholder="Search articles..."
            className="w-full pl-10"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        </div>
      </div>
      
      {/* Categories Widget */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-xl font-serif font-bold mb-4">Categories</h3>
        <ul className="space-y-2">
          {categories.map(category => (
            <li key={category}>
              <Link 
                to={`/blog?category=${category}`}
                className="text-gray-700 hover:text-pkgreen-600 transition-colors flex justify-between items-center"
              >
                <span>{category}</span>
                <span className="text-sm bg-gray-100 px-2 py-0.5 rounded-full">
                  {blogPosts.filter(post => post.category === category).length}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
      
      {/* Recent Posts Widget */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-xl font-serif font-bold mb-4">Recent Posts</h3>
        <div className="space-y-4">
          {recentPosts.map(post => (
            <div key={post.slug} className="flex gap-3">
              <Link to={`/blog/${post.slug}`} className="shrink-0">
                <img 
                  src={post.image} 
                  alt={post.title} 
                  className="w-16 h-16 object-cover rounded"
                />
              </Link>
              <div>
                <h4 className="font-medium line-clamp-2">
                  <Link 
                    to={`/blog/${post.slug}`}
                    className="hover:text-pkgreen-600 transition-colors"
                  >
                    {post.title}
                  </Link>
                </h4>
                <p className="text-sm text-gray-500">
                  {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Tags Widget */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-xl font-serif font-bold mb-4">Tags</h3>
        <div className="flex flex-wrap gap-2">
          {uniqueTags.map(tag => (
            <Link
              key={tag}
              to={`/blog?tag=${tag}`}
              className="bg-gray-100 hover:bg-pkgreen-100 text-gray-700 hover:text-pkgreen-700 px-3 py-1 rounded-full text-sm transition-colors inline-flex items-center"
            >
              <TagIcon className="h-3 w-3 mr-1" />
              {tag}
            </Link>
          ))}
        </div>
      </div>
      
      {/* Newsletter Widget */}
      <div className="bg-pkgreen-50 p-6 rounded-lg border border-pkgreen-100">
        <h3 className="text-xl font-serif font-bold mb-2">Newsletter</h3>
        <p className="text-gray-700 mb-4">Subscribe for landscaping tips and updates</p>
        <div className="space-y-3">
          <Input
            type="email"
            placeholder="Your email address"
          />
          <Button className="w-full bg-pkgreen-600 hover:bg-pkgreen-700">
            Subscribe
          </Button>
        </div>
      </div>
    </aside>
  );
};

export default BlogSidebar;
